/**
 * Graph helpers for the node editor's INPUT pane (ADR-0012).
 *
 * The INPUT pane lets you pick an upstream node and view its output as the
 * input data (n8n's "N nodes back" picker). This computes the upstream
 * nodes ordered by hop-distance. Pure — unit-tested without React.
 */

export interface UpstreamNode {
  id: string;
  label: string;
  kind: string;
  /** Hops from the current node back to this one (1 = immediate input). */
  distance: number;
}

/** Upstream nodes of ``currentId``, ordered by shortest hop distance.
 *  Reverse-BFS over the edges; ties keep the smaller distance. The label
 *  falls back to the id when a node has none. */
export function upstreamNodesByDistance(
  nodes: { id: string; label: string | null; kind: string }[],
  edges: { source: string; target: string }[],
  currentId: string,
): UpstreamNode[] {
  const preds = new Map<string, string[]>();
  for (const e of edges) {
    const arr = preds.get(e.target);
    if (arr) arr.push(e.source);
    else preds.set(e.target, [e.source]);
  }

  const dist = new Map<string, number>();
  const queue: Array<[string, number]> = [[currentId, 0]];
  while (queue.length) {
    const [id, d] = queue.shift()!;
    for (const p of preds.get(id) ?? []) {
      const nd = d + 1;
      if (!dist.has(p) || (dist.get(p) as number) > nd) {
        dist.set(p, nd);
        queue.push([p, nd]);
      }
    }
  }

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: UpstreamNode[] = [];
  for (const [id, distance] of dist) {
    const n = byId.get(id);
    if (n) out.push({ id, label: n.label || id, kind: n.kind, distance });
  }
  return out.sort((a, b) => a.distance - b.distance || a.label.localeCompare(b.label));
}

/** "N node back" / "N nodes back" label for the picker. */
export function nodesBackLabel(distance: number): string {
  return `${distance} node${distance === 1 ? "" : "s"} back`;
}


/** Is this node an ENTRY POINT — i.e. a node a run can start from, with no
 *  inbound edge?  Mirrors the backend rule
 *  (``executor._is_entrypoint_kind``): every trigger (``source`` category)
 *  plus ``read_canonical`` (the canonical event listener). Entry points
 *  must NOT render an input handle and nothing may connect INTO them. */
export function isEntrypointKind(kind: string, category?: string): boolean {
  return category === "source" || kind === "read_canonical";
}


/** Would adding ``source → target`` introduce a cycle?  The graph is a DAG,
 *  so a new edge is illegal when ``target`` can ALREADY reach ``source``
 *  (the new edge would close the loop). Forward-BFS from ``target``. */
export function wouldCreateCycle(
  edges: { source: string; target: string }[],
  source: string,
  target: string,
): boolean {
  if (source === target) return true;
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    const list = adj.get(e.source);
    if (list) list.push(e.target);
    else adj.set(e.source, [e.target]);
  }
  const seen = new Set<string>([target]);
  const stack = [target];
  while (stack.length) {
    const n = stack.pop()!;
    if (n === source) return true; // target reaches source ⇒ cycle
    for (const nxt of adj.get(n) ?? []) {
      if (!seen.has(nxt)) {
        seen.add(nxt);
        stack.push(nxt);
      }
    }
  }
  return false;
}
